export interface SubtractionKeepChallengeConfig {
  readonly id: string;
  readonly skillId: string;
  readonly minuend: number;
  readonly subtrahend: number;
  readonly difference: number;
  readonly hints: readonly string[];
}

export function validateSubtractionKeepChallenge(config: SubtractionKeepChallengeConfig) {
  if (!config.id.trim() || !config.skillId.trim()) {
    throw new RangeError('Subtraction Keep identifiers must not be empty.');
  }
  if (![config.minuend, config.subtrahend, config.difference].every(Number.isInteger)) {
    throw new RangeError('Subtraction Keep values must be whole numbers.');
  }
  if (config.minuend < 10 || config.minuend > 99 || config.subtrahend < 1) {
    throw new RangeError('Subtraction Keep currently supports positive two-digit minuends.');
  }
  if (config.minuend - config.subtrahend !== config.difference) {
    throw new RangeError('Subtraction Keep difference must match the authored equation.');
  }
  if (config.minuend % 10 >= config.subtrahend % 10) {
    throw new RangeError('Every Subtraction Keep challenge must require decomposing one ten.');
  }
  if (config.hints.length < 3 || config.hints.some(hint => !hint.trim())) {
    throw new RangeError('Subtraction Keep requires at least three progressive hints.');
  }
  return config;
}

export const SUBTRACTION_KEEP_CHALLENGES = [
  validateSubtractionKeepChallenge({
    id: 'subtraction-keep-decompose-1',
    skillId: 'math-subtraction-within-100-decompose-ten',
    minuend: 52,
    subtrahend: 27,
    difference: 25,
    hints: [
      'There are not enough ones to remove 7. The exchange vault can open one ten.',
      'Exchange one ten for ten ones. The value stays the same: 4 tens and 12 ones still make 52.',
      'Remove 7 ones and 2 tens, then count what remains.',
    ],
  }),
  validateSubtractionKeepChallenge({
    id: 'subtraction-keep-decompose-2',
    skillId: 'math-subtraction-within-100-decompose-ten',
    minuend: 64,
    subtrahend: 28,
    difference: 36,
    hints: [
      'Four ones are not enough to remove 8 ones.',
      'Trade one of the 6 tens for ten ones. You will have 5 tens and 14 ones.',
      'Remove 8 ones and 2 tens. Count the 3 tens and 6 ones that remain.',
    ],
  }),
  validateSubtractionKeepChallenge({
    id: 'subtraction-keep-decompose-3',
    skillId: 'math-subtraction-within-100-decompose-ten',
    minuend: 91,
    subtrahend: 47,
    difference: 44,
    hints: [
      'One one cannot cover 7 ones, so look for a ten to exchange.',
      'Exchange one ten. Eight tens and 11 ones still have the value 91.',
      'Remove 7 ones and 4 tens. The remaining pieces show the difference.',
    ],
  }),
] as const;

export const SUBTRACTION_KEEP_COMPLETION_EVENT = 'subtraction-keep-gate-restored';
